import React, { useEffect, useState } from 'react';
import Loader from 'react-loaders';
import { useStateSelector } from '../../../../store/selectors';
import { BuyersNdaStatusResponse } from '../../../../slices/marketplace/timeline/models/buyers-nda-status.model';
import { BuyerNdaSign } from '../../../../components/BuyerNdaSign/BuyerNdaSign';
import createNotification from '../../../../utils/createNotification';
import './BuyerNdaGate.scss';

interface BuyerNdaGateProps {
    onNdaSigned: () => void;
}

export const BuyerNdaGate = (props: BuyerNdaGateProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [isLoading, setIsLoading] = useState(true);
    const [ndaStatus, setNdaStatus] = useState<BuyersNdaStatusResponse | null>(null);
    const [documentId, setDocumentId] = useState<number | null>(null);

    useEffect(() => {
        checkNdaStatus();
    }, []);

    const checkNdaStatus = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get('/api/Marketplace/MarketplaceBuyersNda');
            const status: BuyersNdaStatusResponse = response.data;
            setNdaStatus(status);

            if (status.hasSignedLatestNda) {
                props.onNdaSigned();
            } else if (status.hasActiveNdaTemplate) {
                await initiateSigningProcess();
            } else {
                createNotification(
                    'No NDA document is currently available.',
                    'warning'
                );
                setIsLoading(false);
            }
        } catch (error) {
            createNotification(
                'An error occurred while checking NDA status. Please try again.',
                'error'
            );
            setIsLoading(false);
        }
    };

    const initiateSigningProcess = async () => {
        try {
            const response = await axios.post('/api/Marketplace/MarketplaceBuyersNda/sign', []);
            const newDocumentId = response.data;
            setDocumentId(newDocumentId);
        } catch (error) {
            createNotification(
                'An error occurred while preparing the document for signing. Please try again.',
                'error'
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleSignComplete = () => {
        createNotification('NDA signed successfully!', 'success');
        props.onNdaSigned();
    };

    const renderLoader = () => (
        <div className="loading-container">
            <Loader active={true} type="ball-scale-multiple" />
        </div>
    );

    const renderNoTemplate = () => (
        <div className="error-container">
            <div className="error-message">
                No NDA document is currently available.
            </div>
        </div>
    );

    if (isLoading) {
        return <div className="buyer-nda-gate">{renderLoader()}</div>;
    }

    if (!ndaStatus?.hasActiveNdaTemplate) {
        return <div className="buyer-nda-gate">{renderNoTemplate()}</div>;
    }

    return (
        <div className="buyer-nda-gate">
            {documentId ? (
                <div className="nda-sign-container">
                    <BuyerNdaSign
                        documentId={documentId}
                        onDocumentSign={handleSignComplete}
                    />
                </div>
            ) : (
                renderLoader()
            )}
        </div>
    );
};
